import { api, API_BASE_URL } from '../services/api';

/**
 * Check if a date is a holiday for a given currency
 * @param {string} date - Date in YYYY-MM-DD format
 * @param {string} currency - Currency code (defaults to 'LKR')
 * @returns {Promise<Object>} Object with isHoliday flag and holiday details
 */
export const checkHolidayForCurrency = async (date, currency = 'LKR') => {
  try {
    if (!date) {
      return { isHoliday: false, holiday: null, message: '' };
    }

    // Format date to YYYY-MM-DD if needed
    let formattedDate = date;
    if (date instanceof Date) {
      formattedDate = date.toISOString().split('T')[0];
    } else if (typeof date === 'string' && date.includes('/')) {
      // Convert DD/MM/YYYY to YYYY-MM-DD
      const parts = date.split('/');
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
    }

    const response = await api.get(
      `${API_BASE_URL}/holiday-calendar/check-currency/${formattedDate}?currency=${currency}`
    );

    if (response.data.success) {
      return {
        isHoliday: response.data.isHoliday,
        holiday: response.data.data,
        message: response.data.message || ''
      };
    }

    return { isHoliday: false, holiday: null, message: '' };
  } catch (error) {
    console.error('Error checking holiday for currency:', error);
    // On error, don't block - return false
    return { isHoliday: false, holiday: null, message: '' };
  }
};

/**
 * Format holiday message for display
 * @param {Object} holidayCheck - Result from checkHolidayForCurrency
 * @param {string} dateType - Type of date (e.g., 'Trade Date', 'Value Date')
 * @returns {string} Formatted message
 */
export const formatHolidayMessage = (holidayCheck, dateType = 'Date') => {
  if (!holidayCheck || !holidayCheck.isHoliday) {
    return '';
  }

  // If we have a holiday object, use it
  if (holidayCheck.holiday) {
    const { holiday } = holidayCheck;
    const reason = holiday.reason || 'Holiday';
    const fundCentre = holiday.fundCentreName || holiday.fund_centre_name || 'All Fund Centres';
    const date = holiday.date || '';
    
    return `${dateType}${date ? ` (${date})` : ''} is a holiday for currency ${holiday.currency || 'LKR'}. Reason: ${reason}. Fund Centre: ${fundCentre}. Transactions cannot be saved on holidays.`;
  }

  // Fallback: use the message from the API response if available
  if (holidayCheck.message) {
    return `${dateType}: ${holidayCheck.message}`;
  }

  // Last resort: generic message
  return `${dateType} is a holiday. Transactions cannot be saved on holidays.`;
};

